import {DI, DI_VARIATIONS} from '../types'
import {ContainerModule, ContainerModuleLoadOptions} from 'inversify'

import {GetPageMetaUseCase, LoadPageDataUseCase} from '@/src/core/application/page'
import {PageParamsOnFunctionsResolver} from '@/src/core/application/page/page-params-on-functions-resolver'
import {PageParamsResolver} from '@/src/core/application/page/page-params-to-send-resolver'
import {
  LoadPageDataUseCaseInterface,
  PageParamsOnFunctionsResolverInterface,
  PageParamsToSendResolverInterface,
} from '@/src/core/domain/page'
import {PageFetchMapping} from '@/src/core/domain/page/functions/fetch/const'
import {FavouriteFetchOptionsFactoryProxy} from '@/src/core/domain/page/functions/fetch/favourite.fetch'
import {LoginFetchOptionsFactoryProxy} from '@/src/core/domain/page/functions/fetch/login.fetch'
import {OrderFetchOptionsFactoryProxy} from '@/src/core/domain/page/functions/fetch/order.fetch'
import {BasketFetchOptionsFactoryProxy} from '@/src/core/domain/page/functions/fetch/basket.fetch'
import {
  ProfileFetchOptionsFactoryProxy,
  ProfileRootFetchOptionsFactoryProxy,
} from '@/src/core/domain/page/functions/fetch/profile.fetch'
import {
  FetchOptionsFactory,
  FetchOptionsFactoryProxyInterface,
} from '@/src/core/domain/page/functions/fetch/types'
import {registerPageTransforms} from '@/src/core/domain/page/functions/register'
import {PageFunctionsRegistry} from '@/src/core/domain/page/functions/registry'
import {ProfileReservationsFetchOptionsFactoryProxy} from '@/src/core/domain/page/functions/fetch/profile.reservations.fetch'
import {CertificatesFetchOptionsFactoryProxy} from '@/src/core/domain/page/functions/fetch/certificates.fetch'

const initializeModule = ({bind}: ContainerModuleLoadOptions) => {
  bind<FetchOptionsFactoryProxyInterface>(DI_VARIATIONS.FavouriteFetchOptionsFactoryProxy).to(
    FavouriteFetchOptionsFactoryProxy,
  )
  bind<FetchOptionsFactoryProxyInterface>(DI_VARIATIONS.OrderFetchOptionsFactoryProxy).to(
    OrderFetchOptionsFactoryProxy,
  )
  bind<FetchOptionsFactoryProxyInterface>(DI_VARIATIONS.BasketFetchOptionsFactoryProxy).to(
    BasketFetchOptionsFactoryProxy,
  )
  bind<FetchOptionsFactoryProxyInterface>(DI_VARIATIONS.LoginFetchOptionsFactoryProxy).to(
    LoginFetchOptionsFactoryProxy,
  )
  bind<FetchOptionsFactoryProxyInterface>(DI_VARIATIONS.ProfileFetchOptionsFactoryProxy).to(
    ProfileFetchOptionsFactoryProxy,
  )
  bind<FetchOptionsFactoryProxyInterface>(DI_VARIATIONS.CertificatesFetchOptionsFactoryProxy).to(
    CertificatesFetchOptionsFactoryProxy,
  )

  bind<FetchOptionsFactoryProxyInterface>(
    DI_VARIATIONS.ProfileReservationsFetchOptionsFactoryProxy,
  ).to(ProfileReservationsFetchOptionsFactoryProxy)

  bind<FetchOptionsFactoryProxyInterface>(DI_VARIATIONS.StaticFetchOptionsFactoryProxy).to(
    ProfileFetchOptionsFactoryProxy,
  )

  bind<FetchOptionsFactoryProxyInterface>(DI_VARIATIONS.ProfileRootFetchOptionsFactoryProxy).to(
    ProfileRootFetchOptionsFactoryProxy,
  )

  bind<FetchOptionsFactory>(DI.FetchOptionsFactory).toFactory(context => pageFunctionKey => {
    if (pageFunctionKey) {
      const factoryProxy = PageFetchMapping[pageFunctionKey]

      if (factoryProxy) {
        return context.get<FetchOptionsFactoryProxyInterface>(factoryProxy)
      }
    }

    return null
  })

  bind<PageFunctionsRegistry>(DI.PageParamsFunctionsRegistry)
    .toDynamicValue(() => {
      const registry = new PageFunctionsRegistry()
      registerPageTransforms(registry)
      return registry
    })
    .inSingletonScope()
  bind<PageParamsToSendResolverInterface>(DI.PageParamsToSendResolverInterface)
    .to(PageParamsResolver)
    .inRequestScope()
  bind<PageParamsOnFunctionsResolverInterface>(DI.PageParamsOnFunctionsResolverInterface)
    .to(PageParamsOnFunctionsResolver)
    .inRequestScope()
  bind<LoadPageDataUseCaseInterface>(DI.LoadPageDataUseCaseInterface)
    .to(LoadPageDataUseCase)
    .inRequestScope()
  bind<GetPageMetaUseCase>(DI.GetPageMetaUseCase).to(GetPageMetaUseCase).inRequestScope()
}

export const PageDataModule = new ContainerModule(initializeModule)
